import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Edge,
  Node,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, 
  Upload, 
  FileJson,
  Image as ImageIcon,
  LayoutTemplate,
  Table as TableIcon,
  MapPin,
  Network
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useAssetsStore } from '@/stores/assetsStore';
import { useSiteStore } from '@/stores/siteStore';
import { useCloudServiceStore } from '@/stores/cloudServiceStore';
import { AssetNode } from '@/components/assets/AssetNode';
import { NodeCreateDialog } from '@/components/assets/NodeCreateDialog';
import { InspectorDrawer } from '@/components/assets/InspectorDrawer';
import { AssetsTreeView } from '@/components/assets/AssetsTreeView';
import AssetsMapView from '@/components/assets/AssetsMapView';
import { NodeBase, EdgeBase } from '@/types/assets';
import { persistence } from '@/utils/persistence';
import { toast } from 'sonner';
import ELK from 'elkjs/lib/elk.bundled.js';

const nodeTypes = {
  asset: AssetNode,
};

const elk = new ELK();

function AssetsFlow() {
  const { 
    nodes: storeNodes, 
    edges: storeEdges, 
    addEdge: addStoreEdge,
    deleteEdge,
    updateNodePosition,
    loadAssets,
    loadRisks,
    loadEvidence,
    selectedNodeId,
    setSelectedNode
  } = useAssetsStore();

  const [nodes, setNodes, onNodesChange] = useNodesState<Node<NodeBase>>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge<EdgeBase>>([]);
  const [nodeDialogOpen, setNodeDialogOpen] = useState(false);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [clickPosition, setClickPosition] = useState<{ x: number; y: number } | undefined>();
  const [isLayouting, setIsLayouting] = useState(false);

  // Handle node position changes and persist to database
  const handleNodesChange = useCallback((changes: any[]) => {
    onNodesChange(changes);
    
    // Save position changes to database
    changes.forEach((change) => {
      if (change.type === 'position' && change.dragging === false && change.position) {
        updateNodePosition(change.id, change.position);
      }
    });
  }, [onNodesChange, updateNodePosition]);

  // Sync store nodes/edges to React Flow state
  useEffect(() => {
    setNodes(storeNodes);
  }, [storeNodes, setNodes]);

  useEffect(() => {
    setEdges(storeEdges);
  }, [storeEdges, setEdges]);

  // Load stores
  const { loadSites } = useSiteStore();
  const { loadCloudServices } = useCloudServiceStore();

  // Initialize data on first load
  useEffect(() => {
    loadAssets();
    loadRisks();
    loadEvidence();
    loadSites();
    loadCloudServices();
  }, [loadAssets, loadRisks, loadEvidence, loadSites, loadCloudServices]);

  // Removed auto-save that was conflicting with Supabase data

  const onConnect = useCallback(
    (params: Connection) => {
      console.log('Connection attempt:', params);
      const edgeId = `edge-${params.source}-${params.target}-${Date.now()}`;
      const newEdge: Edge<EdgeBase> = {
        id: edgeId,
        source: params.source!,
        target: params.target!,
        sourceHandle: params.sourceHandle,
        targetHandle: params.targetHandle,
        type: 'smoothstep',
        data: {
          id: edgeId,
          type: 'connected_to',
          source: params.source!,
          target: params.target!,
        },
      };
      
      setEdges((eds) => addEdge(newEdge, eds));
      addStoreEdge(newEdge);
      toast.success('Connection created');
    },
    [setEdges, addStoreEdge]
  );

  const onPaneClick = useCallback((event: React.MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.classList.contains('react-flow__pane')) {
      setSelectedNode(null);
      setInspectorOpen(false);
    }
  }, [setSelectedNode]);

  const onPaneContextMenu = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    const bounds = (event.target as HTMLElement).getBoundingClientRect();
    setClickPosition({
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top,
    });
    setNodeDialogOpen(true);
  }, []);

  const handleEdgeClick = useCallback((event: React.MouseEvent, edge: Edge) => {
    event.stopPropagation();
    console.log('Edge clicked:', edge.id);
    setEdges((eds) => eds.filter((e) => e.id !== edge.id));
    deleteEdge(edge.id); // Also remove from store
    toast.success('Connection deleted');
  }, [setEdges, deleteEdge]);

  const handleNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setSelectedNode(node.id);
    setInspectorOpen(true);
  }, [setSelectedNode]);

  const handleAutoLayout = useCallback(async () => {
    setIsLayouting(true);
    
    try {
      const layoutOptions = {
        'elk.algorithm': 'layered',
        'elk.direction': 'DOWN',
        'elk.spacing.nodeNode': '50',
        'elk.layered.spacing.nodeNodeBetweenLayers': '100',
      };

      const graph = {
        id: 'root',
        layoutOptions,
        children: nodes.map((node) => ({
          id: node.id,
          width: 200,
          height: 100,
        })),
        edges: edges.map((edge) => ({
          id: edge.id,
          sources: [edge.source],
          targets: [edge.target],
        })),
      };

      const layoutedGraph = await elk.layout(graph);
      
      const updatedNodes = nodes.map((node) => {
        const layoutNode = layoutedGraph.children?.find((n) => n.id === node.id);
        if (layoutNode) {
          const newPosition = {
            x: layoutNode.x || 0,
            y: layoutNode.y || 0,
          };
          
          // Save position to database
          updateNodePosition(node.id, newPosition);
          
          return {
            ...node,
            position: newPosition,
          };
        }
        return node;
      });
      
      setNodes(updatedNodes);
      
      toast.success('Layout applied successfully');
    } catch (error) {
      console.error('Layout failed:', error);
      toast.error('Failed to apply layout');
    } finally {
      setIsLayouting(false);
    }
  }, [nodes, edges, setNodes, updateNodePosition]);

  const handleExportJSON = useCallback(() => {
    persistence.exportToFile();
    toast.success('Data exported successfully');
  }, []);

  const handleImportJSON = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      persistence.importFromFile(file).then((success) => {
        if (success) {
          toast.success('Data imported successfully');
        } else {
          toast.error('Failed to import data');
        }
      });
    }
  }, []);

  const handleExportPNG = useCallback(() => {
    // This would need a library like html2canvas or react-flow's built-in export
    toast.info('PNG export functionality would be implemented here');
  }, []);

  return (
    <div className="h-full flex">
      {/* Left Toolbar */}
      <div className="w-64 bg-background border-r border-border p-4 space-y-4">
        <div>
          <h3 className="font-semibold mb-3">Tools</h3>
          <div className="space-y-2">
            <Button 
              onClick={() => setNodeDialogOpen(true)} 
              className="w-full justify-start"
              size="sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Node
            </Button>
            
            <div className="text-xs text-muted-foreground px-2 py-1 bg-muted/50 rounded">
              💡 Click connections to delete them
            </div>
            
            <Button 
              onClick={handleAutoLayout}
              disabled={isLayouting}
              variant="outline" 
              className="w-full justify-start"
              size="sm"
            >
              <LayoutTemplate className="h-4 w-4 mr-2" />
              {isLayouting ? 'Layouting...' : 'Auto Layout'}
            </Button>
            
            <Separator />
            
            <Button 
              onClick={handleExportJSON}
              variant="outline" 
              className="w-full justify-start"
              size="sm"
            >
              <FileJson className="h-4 w-4 mr-2" />
              Export JSON
            </Button>
            
            <Button 
              onClick={handleExportPNG}
              variant="outline" 
              className="w-full justify-start"
              size="sm"
            >
              <ImageIcon className="h-4 w-4 mr-2" />
              Export PNG
            </Button>
            
            <div>
              <Label htmlFor="import-json" className="sr-only">Import JSON</Label>
              <Input
                id="import-json"
                type="file"
                accept=".json"
                onChange={handleImportJSON}
                className="hidden"
              />
              <Button 
                onClick={() => document.getElementById('import-json')?.click()}
                variant="outline" 
                className="w-full justify-start"
                size="sm"
              >
                <Upload className="h-4 w-4 mr-2" />
                Import JSON
              </Button>
            </div>
          </div>
        </div>

        <div>
          <h3 className="font-semibold mb-3">Legend</h3>
          <div className="space-y-2 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-0.5 bg-blue-500"></div>
              <span>IT Domain</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-0.5 bg-orange-500"></div>
              <span>OT Domain</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-0.5 bg-purple-500"></div>
              <span>Cloud Domain</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-0.5 bg-red-500 border-dashed"></div>
              <span>Firewall/Conduit</span>
            </div>
          </div>
        </div>
      </div>

      {/* React Flow Canvas */}
      <div className="flex-1 relative">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={handleNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onPaneClick={onPaneClick}
          onPaneContextMenu={onPaneContextMenu as any}
          onNodeClick={handleNodeClick}
          onEdgeClick={handleEdgeClick}
          nodeTypes={nodeTypes as any}
          defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
          minZoom={0.2}
          maxZoom={2}
          fitView
          deleteKeyCode={['Backspace', 'Delete']}
          nodesDraggable={true}
        >
          <Background />
          <Controls />
          <MiniMap 
            nodeStrokeColor="#374151"
            nodeColor="#6b7280"
            maskColor="rgba(0, 0, 0, 0.1)"
          />
        </ReactFlow>

        {/* Status Bar */}
        <div className="absolute bottom-4 left-4 bg-background border border-border rounded-md px-3 py-2 shadow-md">
          <div className="flex items-center gap-4 text-xs text-muted-foreground">
            <span>{nodes.length} nodes</span>
            <span>{edges.length} edges</span>
            {selectedNodeId && (
              <Badge variant="outline" className="text-xs">
                Selected: {nodes.find(n => n.id === selectedNodeId)?.data.label}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Dialogs and Drawers */}
      <NodeCreateDialog
        open={nodeDialogOpen}
        onOpenChange={setNodeDialogOpen}
        position={clickPosition}
      />

      <InspectorDrawer
        open={inspectorOpen}
        onOpenChange={setInspectorOpen}
      />
    </div>
  );
}

function AssetsPage() {
  const { profile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Determine view: URL param > User preference > Default
  const viewFromUrl = searchParams.get('view') as 'table' | 'map' | 'layout' | null;
  const defaultView = profile?.preferences?.assets_default_view || 'layout';
  const [viewMode, setViewMode] = useState<'table' | 'map' | 'layout'>(viewFromUrl || defaultView);
  
  // Update URL when view changes
  useEffect(() => {
    if (viewMode !== viewFromUrl) {
      setSearchParams({ view: viewMode });
    }
  }, [viewMode, viewFromUrl, setSearchParams]);
  
  return (
    <div className="h-screen flex flex-col">
      <div className="border-b border-border p-4 space-y-4">
        <div>
          <h1 className="text-2xl font-bold">Assets Management</h1>
          <p className="text-muted-foreground">
            Interactive asset management with risk assessment and task tracking
          </p>
        </div>
        
        <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as any)}>
          <TabsList>
            <TabsTrigger value="table" className="gap-2">
              <TableIcon className="h-4 w-4" />
              Table View
            </TabsTrigger>
            <TabsTrigger value="map" className="gap-2">
              <MapPin className="h-4 w-4" />
              Map View
            </TabsTrigger>
            <TabsTrigger value="layout" className="gap-2">
              <Network className="h-4 w-4" />
              System Layout
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      
      <div className="flex-1 overflow-hidden">
        {viewMode === 'table' && (
          <div className="h-full p-4">
            <AssetsTreeView />
          </div>
        )}
        {viewMode === 'map' && (
          <AssetsMapView />
        )}
        {viewMode === 'layout' && (
          <ReactFlowProvider>
            <AssetsFlow />
          </ReactFlowProvider>
        )}
      </div>
    </div>
  );
}

export default AssetsPage;